import { datetime } from '@common/services/datetime';
import type {
  LoadBalancingScheduleShiftPreference,
  LoadBalancingShiftPatternPreference,
  SaveWorkerProfileChangeMutationVariables,
} from '@common/graphql';
import type { LoadBalancingWorkerTermType } from '@load-balancing/graphql/queries/load-balancing-worker';
import {
  pickCurrentTerm,
  shiftPreferenceLabel,
} from '@load-balancing/services';

// Maps the service line's configured role abbreviations to Select {value,label}. The picker offers
// only these (a worker only ever carries its service line's roles); labels mirror the abbreviation.
export function buildRoleOptions(
  assignableRoles: readonly string[]
): { value: string; label: string }[] {
  return assignableRoles.map((role) => ({ value: role, label: role }));
}

export const SHIFT_TYPE_OPTIONS: {
  value: LoadBalancingScheduleShiftPreference;
  label: string;
}[] = (['DAY', 'NIGHT', 'EITHER'] as const).map((value) => ({
  value,
  label: shiftPreferenceLabel(value),
}));

// Full pattern vocabulary (unlike shift type, all three values are selectable).
export const SHIFT_PATTERN_OPTIONS: {
  value: LoadBalancingShiftPatternPreference;
  label: string;
}[] = [
  { value: 'CONSECUTIVE', label: 'Consecutive' },
  { value: 'NON_CONSECUTIVE', label: 'Non-consecutive' },
  { value: 'NO_PREFERENCE', label: 'No Shift Pattern' },
];

export type WorkerProfileFormValues = {
  effectiveDate: string; // ISO 'YYYY-MM-DD' or ''
  endDate: string; // ISO 'YYYY-MM-DD' or ''
  accountActive: boolean;
  role: string; // role abbreviation
  ftePercentage: number | null;
  chargeEligible: boolean;
  preferredTime: LoadBalancingScheduleShiftPreference;
  shiftPatternPreference: LoadBalancingShiftPatternPreference;
};

export const ISO_DATE = 'YYYY-MM-DD';

function startOfToday() {
  return datetime().startOf('day');
}

export function isPast(iso: string): boolean {
  return !!iso && datetime(iso).isBefore(startOfToday(), 'day');
}

// True when an end date is set and falls before the effective (start) date. ISO
// 'YYYY-MM-DD' strings sort chronologically, so a lexical comparison is correct.
export function isEndBeforeStart(
  effectiveDate: string,
  endDate: string
): boolean {
  return !!effectiveDate && !!endDate && endDate < effectiveDate;
}

// Scheduled changes that start after the chosen effective date — the ones an open-ended
// (no end date) change would replace. Drives the "Replace future scheduled changes?" warning.
export function laterChanges(
  terms: readonly LoadBalancingWorkerTermType[],
  effectiveDate: string
): LoadBalancingWorkerTermType[] {
  if (!effectiveDate) return [];

  return terms.filter((term) => term.effectiveStartDate > effectiveDate);
}

// Initial form values: from the edited change, or — when creating — from the term active on
// `asOf` for the profile fields, with the dates left blank for the user to choose.
export function initialFormValues({
  terms,
  asOf,
  editingTermId,
}: {
  terms: readonly LoadBalancingWorkerTermType[];
  asOf: string;
  editingTermId?: string | null;
}): WorkerProfileFormValues {
  const editing = editingTermId
    ? terms.find((term) => term.id === editingTermId)
    : undefined;
  const base = editing ?? pickCurrentTerm(terms, asOf);

  return {
    effectiveDate: editing?.effectiveStartDate ?? '',
    endDate: editing?.effectiveEndDate ?? '',
    accountActive: base?.active ?? true,
    role: base?.role ?? '',
    ftePercentage: base?.ftePercentage ?? null,
    chargeEligible: base?.chargeEligible ?? false,
    preferredTime: base?.preferredTime ?? 'EITHER',
    shiftPatternPreference: base?.shiftPatternPreference ?? 'NO_PREFERENCE',
  };
}

export function buildSaveVariables({
  values,
  workerId,
  editingTermId,
  schedulingPeriodId,
}: {
  values: WorkerProfileFormValues;
  workerId: string;
  editingTermId?: string | null;
  schedulingPeriodId?: string | null;
}): SaveWorkerProfileChangeMutationVariables {
  return {
    workerId,
    termId: editingTermId ?? null,
    effectiveStartDate: values.effectiveDate || null,
    effectiveEndDate: values.endDate || null,
    accountActive: values.accountActive,
    role: values.role,
    ftePercentage: values.ftePercentage,
    chargeEligible: values.chargeEligible,
    preferredTime: values.preferredTime,
    shiftPatternPreference: values.shiftPatternPreference,
    schedulingPeriodId: schedulingPeriodId ?? null,
  };
}
